import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import BinanceChart from '@/components/BinanceChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Check, Clock, Play, Square, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const SetupDetail = () => {
  const { setupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchanges, setExchanges] = useState([]);
  const [tradingStatus, setTradingStatus] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSetup();
    if (user) {
      fetchExchanges();
      fetchTradingStatus();
    }
  }, [setupId, user]);

  const fetchExchanges = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/connections`, {
        withCredentials: true
      });
      setExchanges(response.data);
    } catch (error) {
      console.error('Error fetching exchanges:', error);
    }
  };

  const fetchTradingStatus = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/setups/${setupId}/trading-status`, {
        withCredentials: true
      });
      setTradingStatus(response.data);
    } catch (error) {
      console.error('Error fetching trading status:', error);
    }
  };

  const handleActivateTrading = async (exchangeName) => {
    try {
      await axios.post(
        `${backendUrl}/api/setups/${setupId}/activate-trading?exchange_name=${exchangeName}`,
        {},
        { withCredentials: true }
      );
      toast.success('Торговля активирована!');
      fetchTradingStatus();
    } catch (error) {
      console.error('Error activating trading:', error);
      toast.error(error.response?.data?.detail || 'Ошибка активации торговли');
    }
  };

  const handleStopTrading = async () => {
    if (!window.confirm('Остановить автоматическую торговлю?')) {
      return;
    }

    try {
      await axios.post(
        `${backendUrl}/api/setups/${setupId}/stop-trading`,
        {},
        { withCredentials: true }
      );
      toast.success('Торговля остановлена');
      fetchTradingStatus();
    } catch (error) {
      console.error('Error stopping trading:', error);
      toast.error('Ошибка остановки торговли');
    }
  };

  const fetchSetup = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/setups/${setupId}`);
      setSetup(response.data);
    } catch (error) {
      console.error('Error fetching setup:', error);
      toast.error('Ошибка загрузки сетапа');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/3"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!setup) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Toaster position="top-right" />
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-5xl mx-auto">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6 text-center">
              <p className="text-gray-400 mb-4">Сетап не найден</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90"
              >
                На главную
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const finalBalance = setup.steps[setup.steps.length - 1]?.balance_after || setup.initial_balance;
  const profit = finalBalance - setup.initial_balance;
  const profitPercent = ((profit / setup.initial_balance) * 100).toFixed(2);
  
  // Calculate progress by points (entry + exit for each step)
  const totalPoints = setup.steps.length * 2;
  const reachedPoints = setup.steps.reduce((sum, step) => {
    let points = 0;
    if (step.entry_reached) points += 1;
    if (step.reached) points += 1;
    return sum + points;
  }, 0);

  // Get average p_liq_percent from steps
  const avgPLiq = setup.steps.length > 0 
    ? (setup.steps.reduce((sum, s) => sum + (s.p_liq_percent || 0), 0) / setup.steps.length).toFixed(1)
    : '0';

  // Prepare markers for chart (entry and exit points)
  const chartMarkers = setup.steps.flatMap((step, index) => [
    {
      price: step.entry_price,
      label: `Вход ${index + 1}`,
      color: step.direction === 'long' ? '#00ff88' : '#ef4444',
      type: 'entry'
    },
    {
      price: step.exit_price,
      label: `Выход ${index + 1}`,
      color: step.reached ? '#00ff88' : '#fbbf24',
      type: 'exit'
    }
  ]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold text-white">{setup.title}</h1>
              {tradingStatus?.active && (
                <Badge 
                  variant="outline" 
                  className="bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50 animate-pulse"
                >
                  🤖 Торгует
                </Badge>
              )}
              <Badge 
                variant="outline" 
                className={`${
                  setup.status === 'completed' 
                    ? 'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50' 
                    : setup.status === 'active'
                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                    : 'bg-red-500/20 text-red-400 border-red-500/50'
                }`}
              >
                {setup.status === 'completed' ? 'Завершён' : setup.status === 'active' ? 'Активный' : 'Провален'}
              </Badge>
            </div>
            
            {/* Trading buttons - only show for owner */}
            {user && setup.user_id === user.id && (
              <div className="flex gap-2 flex-wrap">
                {exchanges.length > 0 ? (
                  tradingStatus?.active ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleStopTrading}
                      className="border-red-700 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    >
                      <Square className="h-4 w-4 mr-2" />
                      Остановить торговлю
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleActivateTrading(exchanges[0].exchange_name)}
                      className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Использовать на {exchanges[0].exchange_name === 'binance' ? 'Binance' : exchanges[0].exchange_name === 'bybit' ? 'ByBit' : exchanges[0].exchange_name.toUpperCase()}
                    </Button>
                  )
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/exchanges')}
                    className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Подключить биржу
                  </Button>
                )}
              </div>
            )}
          </div>
          <p className="text-gray-400">Создан: {new Date(setup.created_at).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Начальный баланс</p>
                <p className="text-xl font-bold text-white">${setup.initial_balance.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Финальный баланс</p>
                <p className="text-xl font-bold text-white">${finalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Профит</p>
                <p className={`text-xl font-bold ${profit >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                  ${profit.toFixed(2)} ({profitPercent}%)
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">% до ликвидации</p>
                <p className="text-xl font-bold text-white">{avgPLiq}%</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Прогресс</p>
                <p className="text-xl font-bold text-white">{reachedPoints}/{totalPoints}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live BTC Chart with Setup Points */}
        <Card className="bg-[#1a1a1a] border-gray-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white">График BTC с точками сетапа</CardTitle>
          </CardHeader>
          <CardContent>
            <BinanceChart 
              symbol="BTC-USDT"
              timeframe="15m"
              markers={chartMarkers}
              readOnly={true}
            />
          </CardContent>
        </Card>

        {/* Steps */}
        <Card className="bg-[#1a1a1a] border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Шаги сетапа</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {setup.steps.map((step, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border ${
                    step.reached 
                      ? 'border-[#00ff88]/50 bg-[#00ff88]/10' 
                      : 'border-gray-800 bg-[#0a0a0a]'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        step.reached ? 'bg-[#00ff88]' : 'bg-gray-800'
                      }`}>
                        {step.reached ? (
                          <Check className="h-5 w-5 text-black" />
                        ) : (
                          <Clock className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white">Шаг {index + 1}</span>
                          <Badge 
                            variant="outline" 
                            className={`${
                              step.direction === 'long' 
                                ? 'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50' 
                                : 'bg-red-500/20 text-red-400 border-red-500/50'
                            }`}
                          >
                            {step.direction === 'long' ? (
                              <><TrendingUp className="h-3 w-3 mr-1" />Long</>
                            ) : (
                              <><TrendingDown className="h-3 w-3 mr-1" />Short</>
                            )}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-400 space-x-3">
                          <span>Вход: ${step.entry_price.toFixed(2)}</span>
                          <span>•</span>
                          <span>Выход: ${step.exit_price.toFixed(2)}</span>
                          <span>•</span>
                          <span>Плечо: {step.leverage}x</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right md:pl-4">
                      <p className={`text-lg font-semibold ${step.pnl_usd >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                        {step.pnl_usd >= 0 ? '+' : ''}${step.pnl_usd.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-400">
                        Баланс: ${step.balance_after.toFixed(2)}
                      </p>
                      {step.reached && step.reached_at && (
                        <p className="text-xs text-[#00ff88] mt-1">
                          Достигнута {new Date(step.reached_at).toLocaleDateString('ru-RU', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetupDetail;
